/**
 * ProdutoDetalhePanel — o detalhe consolidado do suprimento (redesign 2026-09-15).
 *
 * Slide-over (`SlideOverPanel`, 700px) com cabeçalho rico (amostra, selos,
 * código · tipo · especificação), quatro mini-cards (Saldo, Mínimo, Custo
 * médio, Consumo 30 d) e quatro abas:
 *
 *   Resumo — sugestão de compra (servida pelo backend, ADR-0070) quando
 *   abaixo do mínimo, cadastro e onde está o saldo;
 *   Fornecedores e preços — a lista compartilhada `ProdutoFornecedoresLista`
 *   (mesma verdade da tela de Fornecedores);
 *   Movimentações — `GET /produtos/{id}/movimentos` com saldo após cada
 *   lançamento (acumulado no backend);
 *   Onde é usado — `GET /produtos/{id}/usos`, a mesma regra que recusa a
 *   exclusão.
 *
 * Nada aqui recalcula regra de negócio: consumo, último preço, sugestão e
 * saldo após vêm prontos do backend.
 */
import React, { useEffect, useMemo, useState } from "react";
import { FiAlertTriangle, FiChevronRight, FiEdit2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import DocumentViewerModal from "../../../components/DocumentViewerModal";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { useAuth } from "../../../context/AuthContext";
import { movimentosDoProduto, obterProduto, saldoPorDeposito, usosDoProduto } from "../../../services/estoque";
import { getToneTokens } from "../../../styles/shared";
import { formatarDataLocal, formatarReaisBR, plural } from "../../../utils/dinheiro";
import { ORIGEM_MOV_LABEL, TIPO_MOV_LABEL } from "../helpers";
import { Chip, Mono, Pill } from "../_shared/quadro/styles";
import { Carregando } from "../_shared/Estados";
import ProdutoFornecedoresLista from "../_shared/ProdutoFornecedoresLista";
import { Rotulo } from "../RequisicoesCompra/ui";
import { SelosDoItem, Swatch } from "./celulas";
import { fmtMm, labelCategoria, labelTipo } from "./colunas";
import { coberturaDias, especificacaoDe, infoSaldo, qtd, semFornecedor, ultimoPrecoDe } from "./derivados";

const DIAS_MOVIMENTOS = 90;

// ---------------------------------------------------------------- estilos

const Cabecalho = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  > .texto { flex: 1; min-width: 0; }
  > .texto > .selos { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
  > .texto > .linha { font-size: 12.5px; color: var(--ink-3); margin-top: 4px; display: flex; gap: 8px; flex-wrap: wrap; }
  @media (max-width: 640px) {
    flex-wrap: wrap;
    > .texto > .linha > .sep { display: none; }
    > .texto > .linha > span { flex-basis: 100%; }
    > button { order: 3; }
  }
`;

const MiniCards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
  margin-top: 16px;
  @media (max-width: 640px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  > div { background: var(--surface); border: 1px solid var(--border); border-radius: 10px; padding: 10px 12px; min-width: 0; }
  > div > .lbl { font-size: 10.5px; letter-spacing: .08em; text-transform: uppercase; color: var(--ink-3); font-weight: 600; }
  > div > .val { font-size: 18px; font-weight: 700; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  > div > .val small { font-size: 11px; color: var(--ink-3); font-weight: 500; }
  > div > .sub { font-size: 11px; color: var(--ink-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
`;

const Abas = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 14px;
  border-bottom: 1px solid var(--border);
  flex-wrap: wrap;
  > button {
    height: 36px; padding: 0 12px; border: none; margin-bottom: -1px; background: transparent;
    border-bottom: 2px solid transparent; color: var(--ink-3); font: inherit; font-size: 13px; font-weight: 600;
    cursor: pointer; display: inline-flex; align-items: center; gap: 6px; white-space: nowrap;
    transition: color .15s ease, border-color .15s ease;
  }
  > button:hover { color: var(--ink); }
  > button[aria-selected="true"] { color: var(--accent); border-bottom-color: var(--accent); }
  > button > .n { font-size: 11px; font-weight: 500; color: var(--ink-4); }
  > button > .curto { display: none; }
  @media (max-width: 640px) { > button > .longo { display: none; } > button > .curto { display: inline; } }
`;

const Bloco = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 4px 16px;
  > .linha { display: grid; grid-template-columns: 150px 1fr; gap: 12px; padding: 9px 0; border-bottom: 1px solid var(--divider); font-size: 13px; align-items: baseline; }
  > .linha:last-child { border-bottom: 0; }
  > .linha > .k { font-size: 11px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; color: var(--ink-3); }
  > .linha > .v { color: var(--ink); font-weight: 500; min-width: 0; overflow-wrap: anywhere; }
  > .linha.deposito { grid-template-columns: 150px 1fr auto; align-items: center; }
  @media (max-width: 640px) { > .linha, > .linha.deposito { grid-template-columns: 1fr; gap: 2px; } }
`;

const Sugestao = styled.div`
  border: 1px solid var(--danger-border);
  background: var(--danger-bg);
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
  > .icone { width: 36px; height: 36px; border-radius: 10px; background: var(--danger-solid); color: #fff; display: inline-flex; align-items: center; justify-content: center; flex: 0 0 auto; }
  > .texto { flex: 1; min-width: 200px; }
  > .texto > .t { font-weight: 700; font-size: 13.5px; color: var(--ink); }
  > .texto > .t b { color: var(--danger-text); }
  > .texto > .s { font-size: 12.5px; color: var(--ink-2); margin-top: 2px; }
  > button { height: 34px; padding: 0 14px; border-radius: 9px; border: none; background: var(--ink); color: var(--bg); font: inherit; font-size: 12.5px; font-weight: 700; cursor: pointer; flex: 0 0 auto; }
`;

const Movimentos = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  overflow: hidden;
  > .linha { display: grid; grid-template-columns: 64px 28px 1fr auto auto; gap: 12px; align-items: center; padding: 10px 16px; border-bottom: 1px solid var(--divider); font-size: 13px; min-width: 0; }
  > .linha:last-child { border-bottom: 0; }
  > .linha > .icone { width: 26px; height: 26px; border-radius: 7px; display: inline-flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; }
  > .linha > .texto { min-width: 0; }
  > .linha > .texto > .t { font-weight: 600; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  > .linha > .texto > .s { font-size: 11.5px; color: var(--ink-3); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  > .linha > .qtd { font-weight: 700; white-space: nowrap; }
  > .linha > .apos { font-size: 12px; color: var(--ink-3); min-width: 60px; text-align: right; white-space: nowrap; }
  @media (max-width: 640px) { > .linha { grid-template-columns: 56px 26px 1fr auto; } > .linha > .apos { display: none; } }
`;

const UsoCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: ${({ $link }) => ($link ? "pointer" : "default")};
  > .tag { font-size: 10.5px; font-weight: 600; letter-spacing: .06em; text-transform: uppercase; border-radius: 6px; padding: 3px 8px; flex: 0 0 auto; }
  > .texto { flex: 1; min-width: 0; }
  > .texto > .t { font-weight: 600; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  > .texto > .s { font-size: 11.5px; color: var(--ink-3); }
  > .qtd { font-weight: 600; font-size: 13px; white-space: nowrap; }
`;

const Vazio = styled.div`
  font-size: 12.5px;
  color: var(--ink-3);
  padding: 18px 0;
  text-align: center;
  border: 1px dashed var(--border-strong);
  border-radius: 12px;
`;

// ---------------------------------------------------------------- helpers

const TONE_MOV = { ENTRADA: "success", SAIDA: "danger", AJUSTE: "warning" };
const SINAL_MOV = { ENTRADA: "+", SAIDA: "−", AJUSTE: "±" };

function tituloMovimento(m) {
  const tipo = TIPO_MOV_LABEL[m.tipo] || m.tipo;
  const origem = ORIGEM_MOV_LABEL[m.origem] || m.origem;
  if (m.tipo === "SAIDA" && m.origem === "PRODUCAO") return "Saída para produção";
  if (m.tipo === "ENTRADA" && m.origem === "OC") return "Entrada por OC";
  if (m.origem === "INVENT") return "Ajuste de inventário";
  if (m.origem === "TRANSF") return `Transferência (${tipo.toLowerCase()})`;
  return origem && origem !== "Manual" ? `${tipo} · ${origem}` : tipo;
}

function familiaDe(produto, tipos) {
  const fam = tipos.find((t) => t.codigo === produto.tipo_suprimento)?.familia;
  return fam ? ` · ${fam}` : "";
}

// ---------------------------------------------------------------- componente

export default function ProdutoDetalhePanel({ produtoId, tipos = [], onClose, onEditar, versao = 0 }) {
  const { request, isMaster, hasAllPermissions } = useAuth();
  const navigate = useNavigate();
  const podeEditar = isMaster || hasAllPermissions(["estoque:edit_produto"]);
  const podeRequisitar = isMaster || hasAllPermissions(["compras:criar_requisicao"]);
  const [produto, setProduto] = useState(null);
  const [texturaAberta, setTexturaAberta] = useState(false);
  const [saldos, setSaldos] = useState(null);
  const [movimentos, setMovimentos] = useState(null);
  const [usos, setUsos] = useState(null);
  const [aba, setAba] = useState("resumo");
  const [filtroMov, setFiltroMov] = useState("");

  useEffect(() => {
    if (!produtoId) { setProduto(null); setSaldos(null); setMovimentos(null); setUsos(null); return undefined; }
    let vivo = true;
    setAba("resumo");
    setFiltroMov("");
    obterProduto(request, produtoId).then((p) => { if (vivo) setProduto(p); }).catch(() => { if (vivo) setProduto(null); });
    saldoPorDeposito(request, produtoId).then((s) => { if (vivo) setSaldos(s); }).catch(() => { if (vivo) setSaldos(null); });
    movimentosDoProduto(request, produtoId, { dias: DIAS_MOVIMENTOS })
      .then((r) => { if (vivo) setMovimentos(r?.movimentos || []); })
      .catch(() => { if (vivo) setMovimentos([]); });
    usosDoProduto(request, produtoId).then((u) => { if (vivo) setUsos(u); }).catch(() => { if (vivo) setUsos(null); });
    return () => { vivo = false; };
  }, [request, produtoId, versao]);

  const info = useMemo(() => (produto ? infoSaldo(produto) : null), [produto]);
  const ultimo = useMemo(() => (produto ? ultimoPrecoDe(produto) : null), [produto]);
  const recentes = useMemo(
    () => (movimentos || []).filter((m) => !filtroMov || m.tipo === filtroMov),
    [movimentos, filtroMov],
  );

  const consumo30 = produto ? Number(produto.consumo_30d || 0) : 0;
  const consumo90 = produto ? Number(produto.consumo_90d || 0) : 0;
  const cobertura = info ? coberturaDias(info.saldo, consumo30) : null;
  const sugestao = produto ? Number(produto.sugestao_compra || 0) : 0;
  const especificacao = produto ? especificacaoDe(produto, tipos) : "";

  function criarRequisicao() {
    navigate("/suprimentos/requisicoes", {
      state: {
        novaRequisicao: {
          produto: {
            id: produto.id, nome: produto.nome, codigo: produto.codigo, unidade: produto.unidade, saldo: produto.saldo,
            estoque_minimo: produto.estoque_minimo, sugestao_qtd: sugestao, ultimo_preco: ultimo?.valor ?? null, custo_medio: produto.custo_medio,
          },
          qtd: sugestao,
        },
      },
    });
  }

  const abas = [
    { id: "resumo", longo: "Resumo", curto: "Resumo", n: "" },
    { id: "fornecedores", longo: "Fornecedores e preços", curto: "Fornec.", n: produto?.fornecedores_ativos ?? "" },
    { id: "movimentos", longo: "Movimentações", curto: "Movim.", n: movimentos ? movimentos.length : "" },
    { id: "uso", longo: "Onde é usado", curto: "Uso", n: usos ? usos.total : "" },
  ];

  return (
    <SlideOverPanel
      open={Boolean(produtoId)}
      onClose={onClose}
      title={produto ? produto.nome : "Suprimento"}
      subtitle={produto ? `${produto.codigo} · ${labelTipo(produto, tipos)}` : ""}
      width="min(94vw, 700px)"
    >
      {!produto || !info ? (
        <Carregando linhas={3} colunas={2} />
      ) : (
        <>
          <Cabecalho>
            {produto.textura_url ? (
              <button
                type="button" onClick={() => setTexturaAberta(true)} title="Ver a textura ampliada"
                aria-label={`Ver a textura de ${produto.nome}`}
                style={{ padding: 0, border: 0, background: "none", cursor: "zoom-in", borderRadius: 12 }}
              >
                <Swatch produto={produto} size={52} />
              </button>
            ) : (
              <Swatch produto={produto} size={52} />
            )}
            <div className="texto">
              <div className="selos">
                <Pill $tone={produto.ativo ? "success" : "neutral"} $dot>{produto.ativo ? "Ativo" : "Inativo"}</Pill>
                <SelosDoItem produto={{ ...produto, ativo: true }} semFornecedor={semFornecedor(produto)} />
                {info.abaixo ? <Pill $tone="danger">abaixo do mínimo</Pill> : null}
              </div>
              <div className="linha">
                <Mono style={{ color: "var(--ink-2)" }}>{produto.codigo}</Mono>
                <span className="sep">·</span>
                <span>{labelTipo(produto, tipos)}</span>
                <span className="sep">·</span>
                <span>{especificacao}</span>
              </div>
            </div>
            {podeEditar && onEditar ? (
              <button type="button" className="btn btn--secondary btn--sm" onClick={() => onEditar(produto)} style={{ flex: "0 0 auto" }}>
                <FiEdit2 size={13} /> Editar
              </button>
            ) : null}
          </Cabecalho>

          <MiniCards>
            <div>
              <div className="lbl">Saldo</div>
              <div className="val" style={{ color: info.abaixo ? "var(--danger-text)" : undefined }}>{qtd(info.saldo)} <small>{produto.unidade}</small></div>
              <div className="sub">{info.sub}</div>
            </div>
            <div>
              <div className="lbl">Mínimo</div>
              <div className="val">{qtd(info.min)} <small>{produto.unidade}</small></div>
              <div className="sub">{cobertura === null ? "sem consumo em 30 d" : `cobertura ${plural(cobertura, "dia", "dias")}`}</div>
            </div>
            <div>
              <div className="lbl">Custo médio</div>
              <div className="val">{Number(produto.custo_medio) > 0 ? formatarReaisBR(produto.custo_medio) : "—"}</div>
              <div className="sub">{ultimo ? `último ${formatarReaisBR(ultimo.valor)}` : "sem preço de fornecedor"}</div>
            </div>
            <div>
              <div className="lbl">Consumo 30 d</div>
              <div className="val">{qtd(consumo30)} <small>{produto.unidade}</small></div>
              <div className="sub">{consumo90 > 0 ? `média 90 d: ${qtd(Math.round((consumo90 / 3) * 10) / 10)} / 30 d` : "sem consumo"}</div>
            </div>
          </MiniCards>

          <Abas role="tablist" aria-label="Seções do suprimento">
            {abas.map((a) => (
              <button key={a.id} type="button" role="tab" aria-selected={aba === a.id} onClick={() => setAba(a.id)}>
                <span className="longo">{a.longo}</span><span className="curto">{a.curto}</span>
                {a.n !== "" ? <span className="n">{a.n}</span> : null}
              </button>
            ))}
          </Abas>

          <div style={{ display: "flex", flexDirection: "column", gap: 16, paddingTop: 16 }}>
            {aba === "resumo" ? (
              <>
                {info.abaixo && sugestao > 0 ? (
                  <Sugestao role="note">
                    <span className="icone"><FiAlertTriangle size={16} /></span>
                    <div className="texto">
                      <div className="t">Sugestão de compra: <b>{qtd(sugestao)} {produto.unidade}</b></div>
                      <div className="s">
                        Repõe o mínimo + consumo de 30 dias.
                        {ultimo ? (
                          <> Último preço: <b>{ultimo.fornecedor || "—"}</b> · {formatarReaisBR(ultimo.valor)}{ultimo.data ? ` · ${formatarDataLocal(ultimo.data)}` : ""} · ≈ {formatarReaisBR(sugestao * ultimo.valor)}</>
                        ) : " Nenhum fornecedor com preço: vincule um na aba Fornecedores."}
                      </div>
                    </div>
                    {podeRequisitar ? <button type="button" onClick={criarRequisicao}>Criar requisição</button> : null}
                  </Sugestao>
                ) : null}

                <div>
                  <Rotulo>Cadastro</Rotulo>
                  <Bloco>
                    <div className="linha"><span className="k">Tipo</span><span className="v">{labelTipo(produto, tipos)}{familiaDe(produto, tipos)}</span></div>
                    <div className="linha"><span className="k">Unidade</span><span className="v">{produto.unidade}{produto.m2_por_unidade ? ` · ${qtd(produto.m2_por_unidade)} m² por unidade` : ""}</span></div>
                    {(produto.largura_mm || produto.comprimento_mm || produto.espessura_mm) ? (
                      <div className="linha"><span className="k">Medidas</span><span className="v">{fmtMm(produto.comprimento_mm)} × {fmtMm(produto.largura_mm)} × {fmtMm(produto.espessura_mm)} mm{produto.tem_veio === true ? " · com veio" : produto.tem_veio === false ? " · sem veio" : ""}</span></div>
                    ) : null}
                    {(produto.atributos || {}).cor ? <div className="linha"><span className="k">Cor / padrão</span><span className="v">{produto.atributos.cor}</span></div> : null}
                    {produto.fabricante ? <div className="linha"><span className="k">Fabricante</span><span className="v">{produto.fabricante}{produto.referencia_fabricante ? ` · ref. ${produto.referencia_fabricante}` : ""}</span></div> : null}
                    {produto.ncm ? <div className="linha"><span className="k">NCM</span><span className="v">{produto.ncm}</span></div> : null}
                    <div className="linha"><span className="k">Categoria</span><span className="v">{labelCategoria(produto.categoria)}</span></div>
                    <div className="linha"><span className="k">Origem</span><span className="v">{produto.origem_cadastro_display || produto.origem_cadastro || "—"}</span></div>
                    <div className="linha"><span className="k">Atualizado</span><span className="v">{formatarDataLocal(produto.updated_at)}</span></div>
                    {produto.descricao ? <div className="linha"><span className="k">Descrição</span><span className="v">{produto.descricao}</span></div> : null}
                  </Bloco>
                </div>

                <div>
                  <Rotulo>Onde está o saldo</Rotulo>
                  <Bloco>
                    {!saldos ? (
                      <div className="linha"><span className="k">Depósitos</span><span className="v">—</span></div>
                    ) : (saldos.por_deposito || []).filter((l) => Number(l.saldo) !== 0).length === 0 ? (
                      <div className="linha"><span className="k">Depósitos</span><span className="v">Sem saldo em nenhum depósito.</span></div>
                    ) : (saldos.por_deposito || []).filter((l) => Number(l.saldo) !== 0).map((l) => (
                      <div className="linha deposito" key={l.deposito_id}>
                        <Mono style={{ color: "var(--ink-2)" }}>{l.deposito_codigo || "—"}</Mono>
                        <span style={{ color: "var(--ink-3)", fontSize: 12.5 }}>{l.deposito_nome}</span>
                        <span style={{ fontWeight: 600 }}>{qtd(l.saldo)} {produto.unidade}</span>
                      </div>
                    ))}
                  </Bloco>
                </div>
              </>
            ) : null}

            {aba === "fornecedores" ? <ProdutoFornecedoresLista produtoId={produto.id} /> : null}

            {aba === "movimentos" ? (
              <>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                  {[["", "Todas"], ["ENTRADA", "Entradas"], ["SAIDA", "Saídas"], ["AJUSTE", "Ajustes"]].map(([v, l]) => (
                    <Chip key={v || "todas"} type="button" $on={filtroMov === v} onClick={() => setFiltroMov(v)} style={{ height: 26 }}>{l}</Chip>
                  ))}
                  <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--ink-3)" }}>últimos {DIAS_MOVIMENTOS} dias</span>
                </div>
                {movimentos === null ? (
                  <Carregando linhas={3} colunas={2} />
                ) : recentes.length === 0 ? (
                  <Vazio>Nenhuma movimentação nos últimos {DIAS_MOVIMENTOS} dias.</Vazio>
                ) : (
                  <Movimentos>
                    {recentes.map((m) => {
                      const t = getToneTokens(null, TONE_MOV[m.tipo] || "neutral");
                      const sub = [m.observacao, m.deposito_nome, m.criado_por_nome].filter(Boolean).join(" · ");
                      return (
                        <div className="linha" key={m.id}>
                          <Mono $size={11.5} style={{ color: "var(--ink-3)" }}>{formatarDataLocal(m.created_at).slice(0, 5)}</Mono>
                          <span className="icone" style={{ background: t.background, color: t.text }}>{SINAL_MOV[m.tipo] || "·"}</span>
                          <div className="texto">
                            <div className="t" title={tituloMovimento(m)}>{tituloMovimento(m)}</div>
                            <div className="s" title={sub}>{sub || "—"}</div>
                          </div>
                          <span className="qtd" style={{ color: t.text }}>{SINAL_MOV[m.tipo] || ""}{qtd(Math.abs(Number(m.quantidade)))}</span>
                          <span className="apos">saldo {qtd(m.saldo_apos)}</span>
                        </div>
                      );
                    })}
                  </Movimentos>
                )}
              </>
            ) : null}

            {aba === "uso" ? (
              <>
                <div style={{ fontSize: 12.5, color: "var(--ink-3)" }}>
                  Projetos, sobras, reservas, requisições e ordens de compra que referenciam este item. É o que impede a exclusão — aqui você vê o motivo antes de tentar.
                </div>
                {usos === null ? (
                  <Carregando linhas={2} colunas={2} />
                ) : usos.total === 0 ? (
                  <Vazio>Nenhum projeto, requisição, ordem de compra, sobra ou reserva referencia este item.</Vazio>
                ) : (
                  <>
                    {usos.grupos.map((g) => {
                      const t = getToneTokens(null, g.tone);
                      return g.itens.map((u) => (
                        <UsoCard
                          key={`${g.tipo}-${u.id}`}
                          $link={Boolean(u.rota)}
                          role={u.rota ? "link" : undefined}
                          onClick={u.rota ? () => navigate(u.rota) : undefined}
                        >
                          <span className="tag" style={{ background: t.background, color: t.text }}>{g.tipo}</span>
                          <div className="texto"><div className="t" title={u.titulo}>{u.titulo}</div><div className="s">{u.sub}</div></div>
                          <span className="qtd">{u.quantidade ? `${u.quantidade} ${g.tipo === "Sobra" ? "" : produto.unidade}`.trim() : ""}</span>
                          {u.rota ? <FiChevronRight size={14} style={{ color: "var(--ink-4)" }} /> : null}
                        </UsoCard>
                      ));
                    })}
                    {usos.grupos.some((g) => g.n > g.itens.length) || usos.outros.length ? (
                      <div style={{ fontSize: 11.5, color: "var(--ink-4)" }}>
                        {usos.grupos.filter((g) => g.n > g.itens.length).map((g) => `${g.tipo}: mostrando ${g.itens.length} de ${g.n}`).join(" · ")}
                        {usos.outros.length ? `${usos.grupos.some((g) => g.n > g.itens.length) ? " · " : ""}Também em: ${usos.outros.map((o) => `${o.n} ${o.tipo}`).join(", ")}` : ""}
                      </div>
                    ) : null}
                  </>
                )}
              </>
            ) : null}
          </div>
        </>
      )}
      {texturaAberta && produto?.textura_url ? (
        <DocumentViewerModal
          doc={{ nome: `Textura ${produto.codigo}.jpg`, url: produto.textura_url, mime: "image/jpeg" }}
          onClose={() => setTexturaAberta(false)}
        />
      ) : null}
    </SlideOverPanel>
  );
}
